/**
 * Native SIP calling for iOS, backed by the `NativeVoice` Capacitor plugin
 * (LinphoneManager.swift). Implements the same Device/Call contract as
 * voiceClient.js's SignalWireWebDevice so it can be swapped in without the
 * UI knowing which one it has.
 *
 * WHY THIS EXISTS
 * Call media inside WKWebView is pinned to the loudspeaker. WebKit owns the
 * output route from its own process and app code cannot move it — proven
 * across four separate approaches (see plans/TRUVA_MIGRATION_CLEANUP.md §2).
 * Running the media natively fixes it: verified on device, a live call
 * reports outputs=["Receiver"] (the earpiece) and is audible at the ear.
 *
 * SCOPE — outbound and inbound direct SIP calls.
 * Product rule: softphone must NEVER disconnect unless the user logs off.
 * Long OS suspend can still drop the SIP TLS socket; recovery is automatic:
 *   - native SipRegistrationKeeper re-REGISTERs from stored creds on Cleared
 *   - Capacitor/app resume calls refreshRegistration()
 *   - iOS VoIP push → CallKit → Linphone + wake-ack re-ring
 *   - Android InboundRingService (foreground poll) + optional FCM
 * Credentials are cleared only on explicit logout (clearStoredCredential).
 *
 * Enable with:  localStorage.truvaNativeVoice = '1'   (or window.__NATIVE_VOICE = true)
 */
import { Capacitor, registerPlugin } from '@capacitor/core';

export const NativeVoice = registerPlugin('NativeVoice');

export { NativeCall };

export function isNativeVoiceAvailable() {
  const p = Capacitor.getPlatform();
  return Capacitor.isNativePlatform() && (p === 'ios' || p === 'android');
}

/**
 * On by default on iOS — the WebView path is loudspeaker-only and cannot be
 * fixed from app code, so leaving it as the default meant shipping a phone
 * app you cannot hold to your ear.
 *
 * Escape hatch both ways, since this path is newer than the WebView one:
 *   localStorage.truvaNativeVoice = '0'   → force the WebView path back
 *   localStorage.truvaNativeVoice = '1'   → force native (e.g. while testing)
 * createVoiceDevice also falls back to the WebView device automatically if
 * native init throws, so a failure here degrades to the old behaviour rather
 * than to a dead call button.
 */
// How long after we ask native to (re-)register that a Cleared event is
// treated as the registration clearing itself, not a lost registration.
const OWN_REGISTER_WINDOW_MS = 5000;

export function isNativeVoiceEnabled() {
  if (!isNativeVoiceAvailable()) return false;
  try {
    if (typeof window !== 'undefined' && window.__NATIVE_VOICE === true) return true;
    if (typeof window !== 'undefined' && window.__NATIVE_VOICE === false) return false;
    const pref = window?.localStorage?.getItem('truvaNativeVoice');
    if (pref === '0') return false;
    if (pref === '1') return true;
    // Default ON for iOS, which has been verified end-to-end on a device:
    // registration, a live call, and outputs=["Receiver"] (the earpiece).
    //
    // Default OFF for Android until the same has been done there. The code
    // is the same shape and the Java mirrors the iOS fixes, but "compiles
    // and mirrors a working implementation" is not evidence that audio
    // flows on real hardware — that assumption is exactly what this whole
    // effort had to disprove on iOS.
    return Capacitor.getPlatform() === 'ios';
  } catch (_) {
    // Storage unavailable (private mode, etc.) — still prefer working audio.
    return Capacitor.getPlatform() === 'ios';
  }
}

class EventBus {
  constructor() { this._listeners = {}; }
  on(ev, cb) { (this._listeners[ev] = this._listeners[ev] || []).push(cb); return this; }
  off(ev, cb) { this._listeners[ev] = (this._listeners[ev] || []).filter(f => f !== cb); return this; }
  emit(ev, ...args) {
    const n = (this._listeners[ev] || []).length;
    console.warn(`[NativeVoice-DIAG] emit('${ev}') on ${this.constructor.name} — ${n} listener(s)`);
    (this._listeners[ev] || []).forEach(cb => {
      try { cb(...args); } catch (e) { console.error(`[NativeVoice] listener ${ev} threw:`, e); }
    });
  }
}

/** Mirrors voiceClient.js's Call wrapper. */
class NativeCall extends EventBus {
  constructor(meta = {}) {
    super();
    this._status = 'connecting';
    this._finished = false;
    this.meta = meta;
    // The UI reads From for inbound (who's calling) and To for outbound.
    this.parameters = { To: meta.to, From: meta.from, CallSid: meta.callSid || '' };
    this.direction = meta.inbound ? 'INCOMING' : 'OUTGOING';
    this._pendingDtmf = '';
    this._held = false;
  }

  status() { return this._status; }

  _onState(state) {
    console.warn(`[NativeVoice-DIAG] _onState('${state}') status=${this._status} finished=${this._finished} instance=${this._diagId || (this._diagId = Math.random().toString(36).slice(2, 8))}`);
    // linphone Call.State names, from LinphoneManager's delegate.
    if (state === 'OutgoingRinging' || state === 'OutgoingEarlyMedia') {
      if (this._status === 'connecting') {
        this._status = 'ringing';
        this.emit('ringing');
      }
    } else if (state === 'Paused' || state === 'PausedByRemote' || state === 'Pausing') {
      // Second-call interruption / audio-focus loss / call waiting. The call
      // is NOT over - it is on hold. Never disconnect, stop recording, or
      // tear down Live Translate from here.
      this._setHeld(true);
    } else if (state === 'Resuming') {
      this._setHeld(false);
    } else if (state === 'Connected' || state === 'StreamsRunning') {
      this._setHeld(false);
      if (this._status !== 'open') {
        this._status = 'open';
        this.emit('accept', this);
      }
    } else if (state === 'End' || state === 'Released' || state === 'Error') {
      this._finish();
    }
  }

  /** Local held flag mirror. isHeld() is true while the call is paused. */
  isHeld() { return !!this._held; }

  /** Only emits a transition (hold/unhold) if the held state actually changed. */
  _setHeld(held) {
    held = !!held;
    if (this._held === held) return;
    this._held = held;
    this.emit(held ? 'hold' : 'unhold', this);
  }

  /** Native plugin-driven hold/resume (audio-focus loss, call waiting, or the
   *  pending Telecom ConnectionService). Never triggers a hangup. */
  setHeld(held) {
    NativeVoice.setHeld({ held: !!held })
      .then(() => this._setHeld(!!held))
      .catch(e => console.warn('[NativeVoice] setHeld:', e));
  }

  _finish() {
    if (this._finished) return;
    this._finished = true;
    this._status = 'closed';
    this._held = false;
    this.emit('disconnect', this);
  }

  accept() {
    // Optimistic: the UI transitions on 'accept', and the authoritative
    // Connected/StreamsRunning event follows from the native side and is
    // idempotent (_onState won't re-emit once status is already 'open').
    NativeVoice.answer()
      .then(() => {
        if (this._status !== 'open') {
          this._status = 'open';
          this.emit('accept', this);
        }
      })
      .catch(e => console.warn('[NativeVoice] answer:', e));
  }

  reject() {
    NativeVoice.hangup().catch(() => {});
    this._finish();
  }

  disconnect() {
    NativeVoice.hangup().catch(e => console.warn('[NativeVoice] hangup:', e));
    // The joining leg has endConferenceOnExit=False (so 3-way calls survive
    // one party dropping) — our hangup alone leaves the caller connected,
    // hearing dead air, until their own leg eventually times out on its
    // own. End the conference by name directly rather than relying on a
    // sibling-lookup that assumes call_sid identifies our own leg, and
    // looking the conference up by friendly_name (see git history) proved
    // unreliable — SignalWire can create multiple conference resources
    // under the same friendly_name over one call's lifetime. Ending the
    // caller's own call by its own sid is direct and unambiguous.
    const pstnCallSid = this.parameters?.CallSid;
    const { apiBase, authToken } = this.meta || {};
    if (pstnCallSid && apiBase && authToken) {
      fetch(`${apiBase}/api/softphone/end-inbound`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ pstn_call_sid: pstnCallSid }),
      }).catch((e) => console.warn('[NativeVoice] end-inbound failed:', e?.message || e));
    }
    this._finish();
  }

  mute(m) {
    NativeVoice.setMuted({ muted: !!m }).catch(e => console.warn('[NativeVoice] setMuted:', e));
  }

  /**
   * Route audio to the loudspeaker or back to the earpiece.
   *
   * Note this direction actually works here. Under WKWebView the same
   * request succeeded and the route stayed on Speaker anyway.
   */
  setSpeaker(enabled) {
    return NativeVoice.setSpeaker({ enabled: !!enabled })
      .catch(e => console.warn('[NativeVoice] setSpeaker:', e));
  }

  sendDigits(d) {
    const digits = String(d ?? '');
    if (!digits) return;
    const { apiBase, authToken } = this.meta || {};
    const sid = this.parameters?.CallSid || this.meta?.callSid || '';
    if (!apiBase || !authToken) {
      console.warn('[NativeVoice] sendDigits missing apiBase/authToken on NativeCall.meta');
      return;
    }
    if (!sid) {
      // Orchestrator/PSTN sid is polled after placeCall; queue tones so an
      // early IVR prompt is not dropped.
      this._pendingDtmf = (this._pendingDtmf || '') + digits;
      return;
    }
    this._postDtmf(apiBase, authToken, sid, digits);
  }

  flushPendingDtmf() {
    const digits = this._pendingDtmf;
    if (!digits) return;
    this._pendingDtmf = '';
    this.sendDigits(digits);
  }

  _postDtmf(apiBase, authToken, sid, digits) {
    fetch(`${apiBase}/api/call/dtmf`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true',
      },
      body: JSON.stringify({ call_sid: sid, digits }),
    }).catch((e) => console.warn('[NativeVoice] sendDigits failed:', e?.message || e));
  }
}

class NativeVoiceDevice extends EventBus {
  constructor(opts = {}) {
    super();
    this._opts = opts;
    this._currentCall = null;
    this._registered = false;
    this._registerWaiters = [];
    // addListener() registers directly on the Capacitor plugin bridge, not
    // on this JS instance — destroy() used to skip removing these, so every
    // device re-init (a normal, repeated part of the app's startup effect)
    // left the previous instance's listeners still live. Multiple
    // NativeVoiceDevice instances then all processed the same native events
    // independently, each with its own _currentCall — confirmed on device:
    // the call object the UI ended up listening to and the one actually
    // receiving state updates could be two different instances, so
    // 'disconnect' never fired and the UI stuck on "Connected" after
    // hangup. Track the handles so destroy() can actually remove them.
    this._listenerHandles = [];

    // Native-issued hold state (Android audio-focus loss, iOS CallKit hold,
    // or a second Truva call). Keeps the call alive and mirrors the held
    // flag onto the current NativeCall. Idempotent on the call side.
    this._listenerHandles.push(NativeVoice.addListener('callHeld', ({ held }) => {
      console.warn(`[NativeVoice] callHeld=${held}`);
      this._currentCall?._setHeld(!!held);
    }));

    this._listenerHandles.push(NativeVoice.addListener('registrationStateChanged', ({ state, message }) => {
      console.warn(`[NativeVoice] registration state=${state} msg=${message}`);
      if (state === 'Ok') {
        clearTimeout(this._registrationFailTimer);
        this._registered = true;
        // Our re-register has completed: from here on a Cleared is a real
        // loss of the registration and must trigger recovery (always-on).
        this._ownRegisterUntil = 0;
        this._flushRegisterWaiters(null);
        this.emit('registered');
      } else if (state === 'Cleared') {
        // Cleared just means "not currently registered" — Linphone fires
        // it as normal housekeeping (e.g. clearing a prior registration)
        // on the way to a fresh one, not only on a genuine failure. It was
        // previously treated the same as Failed, so every register() call
        // that happened to clear a prior state first surfaced a spurious
        // "Init failed" error on screen a moment before the real Ok came
        // through and quietly fixed it — exactly the flash-then-clear the
        // status bar showed on every login.
        //
        // Product rule: stay online until logout. Emit unregistered so the
        // UI gates dial/Online, and ask native to re-REGISTER from store
        // (debounced natively). Do NOT wait for the user to open the app.
        this._registered = false;
        this.emit('unregistered');
        // A re-REGISTER we asked for (register()/refreshRegistration())
        // makes Linphone clear the prior registration first, so a Cleared
        // inside that window is our own housekeeping, not a lost
        // registration. Scheduling another refresh for it re-registers,
        // which clears again, which refreshes again: a ~1/s loop that left
        // the line flapping Online/Connecting (seen on device 2026-10-05).
        if (Date.now() < (this._ownRegisterUntil || 0)) {
          console.warn('[NativeVoice] Cleared during our own re-register — not scheduling another refresh');
        } else {
          this._scheduleNativeRefresh('cleared');
        }
      } else if (state === 'Failed') {
        this._registered = false;
        this.emit('unregistered');
        this._scheduleNativeRefresh('failed');
        // Same flash-then-clear problem as Cleared above: at cold app
        // launch the network interface often isn't up yet, so the first
        // REGISTER attempt fails with a transient "io error" and Linphone
        // retries on its own a moment later — usually landing on Ok before
        // a user could act on the error anyway. Give that retry a window
        // before surfacing anything; a failure that's still standing after
        // it is a real one.
        clearTimeout(this._registrationFailTimer);
        this._registrationFailTimer = setTimeout(() => {
          if (this._registered) return;
          const err = new Error(`SIP registration ${state}: ${message}`);
          this._flushRegisterWaiters(err);
          this.emit('error', err);
        }, 3000);
      }
    }));

    this._listenerHandles.push(NativeVoice.addListener('callStateChanged', ({ state, remoteAddress, message }) => {
      console.warn(`[NativeVoice] call state=${state} remote=${remoteAddress} msg=${message}`);
      console.warn(`[NativeVoice-DIAG] device instance=${this._diagId || (this._diagId = Math.random().toString(36).slice(2, 8))} currentCall=${this._currentCall ? (this._currentCall._diagId || 'no-id-yet') : 'null'}`);

      // Inbound. linphone reports IncomingReceived once the INVITE lands;
      // the native side has already stashed it as the current call, so
      // accept()/reject() on the object below act on the right one.
      if (state === 'IncomingReceived' && !this._currentCall) {
        const call = new NativeCall({
          from: remoteAddress,
          inbound: true,
          apiBase: this._opts.apiBase,
          authToken: this._opts.authToken,
        });
        call._status = 'pending';
        this._currentCall = call;
        this.emit('incoming', call);
        // linphone only knows the SIP dialog, not our application-level
        // PSTN call_sid — Live Translate needs that to attach to the right
        // media stream. One-shot (not repeating) lookup of the backend's
        // pending-ring record, best-effort; this is deliberately NOT the
        // polling+beginSoftphoneInbound mechanism (that's WebView-only and
        // racing it against a real native call is what broke calls
        // outright — see the comment on that poll's useEffect in App.js).
        const { apiBase, authToken } = this._opts;
        if (apiBase && authToken) {
          fetch(`${apiBase}/api/softphone/pending-inbound`, {
            headers: { Authorization: `Bearer ${authToken}` },
          })
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
              if (data?.pending && data.pstn_call_sid && this._currentCall === call) {
                call.parameters.CallSid = data.pstn_call_sid;
                call.flushPendingDtmf();
              }
            })
            .catch(() => {});
        }
        return;
      }

      this._currentCall?._onState(state);
      if (state === 'End' || state === 'Released' || state === 'Error') {
        this._currentCall = null;
      }
    }));
  }

  _flushRegisterWaiters(err) {
    const waiters = this._registerWaiters;
    this._registerWaiters = [];
    waiters.forEach(({ resolve, reject }) => (err ? reject(err) : resolve()));
  }

  /**
   * Fetch this user's SIP credential from the backend and register.
   *
   * The credential is per-identity and issued by GET /api/sip/credential —
   * deliberately not a shared secret compiled into the app, which would let
   * any user extract it and place calls billed to the account. The password
   * is returned exactly once, on first provision; it is kept here only for
   * the lifetime of this device object.
   */
  async register() {
    this._ownRegisterUntil = Date.now() + OWN_REGISTER_WINDOW_MS;
    const { apiBase, authToken } = this._opts;
    if (!apiBase) throw new Error('NativeVoice: apiBase required to fetch SIP credentials');

    // A prior successful register() persists the password to Keychain
    // (SignalWireVoicePlugin.register). Reuse it on ordinary relaunches so
    // we don't rotate every cold start — the backend only returns the SIP
    // password once per provision, and rotating invalidates the old
    // password immediately. A rotation that lands while a call is
    // ringing/active breaks the live registration mid-call.
    // Fetch-or-rotate a credential from the backend. Used for the initial
    // attempt (when nothing is in Keychain) and as the fallback when a
    // stored credential turns out to be stale (see below) — in both cases
    // we end up with a credential SignalWire just (re)issued.
    const fetchFreshCredential = async () => {
      const resp = await fetch(`${apiBase}/api/sip/credential`, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        credentials: 'include',
      });
      if (!resp.ok) {
        const body = await resp.text().catch(() => '');
        throw new Error(`NativeVoice: /api/sip/credential ${resp.status} ${body.slice(0, 200)}`);
      }
      const fetched = await resp.json();
      if (!fetched.password) {
        // Expected on any call after the first (no stored Keychain
        // credential either, e.g. fresh install): the backend issues the
        // password once and does not store it, so rotate to get a fresh one.
        console.warn('[NativeVoice] no password returned (already provisioned) — rotating');
        const rot = await fetch(`${apiBase}/api/sip/credential/rotate`, {
          method: 'POST',
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
          credentials: 'include',
        });
        if (!rot.ok) throw new Error(`NativeVoice: credential rotate failed ${rot.status}`);
        Object.assign(fetched, await rot.json());
      }
      return fetched;
    };

    // The Keychain is a single global slot, not scoped per logged-in
    // identity — so on a device that's ever logged into more than one
    // Truva account, a stored credential from the PREVIOUS identity is
    // silently reused after switching to a new one, registering the
    // native SIP client under the wrong username entirely. The backend
    // never sees a mismatch (Linphone registers fine under the stale
    // identity), but every subsequent call this device places can't be
    // matched to the pending session filed under the identity the user
    // is actually logged in as, so /voice never dials the real
    // destination out — confirmed on device by a From: header showing a
    // different user's SIP username than the one logged into the app.
    // GET /api/sip/credential is safe to call unconditionally: without
    // rotate it always returns the current identity's username (and
    // domain) even when password comes back null for an already-issued
    // endpoint, so this costs one cheap request, not a rotation.
    let expected = null;
    try {
      const resp = await fetch(`${apiBase}/api/sip/credential`, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        credentials: 'include',
      });
      if (resp.ok) expected = await resp.json();
    } catch (e) {
      console.warn('[NativeVoice] identity check fetch failed, trusting stored credential if present:', e?.message || e);
    }

    let cred = null;
    let usedStoredCredential = false;
    try {
      const stored = await NativeVoice.getStoredCredential();
      if (stored && stored.found) {
        if (expected && expected.username && stored.username !== expected.username) {
          console.warn(`[NativeVoice] stored credential is for ${stored.username}, current identity expects ${expected.username} — discarding stale credential`);
        } else {
          cred = {
            username: stored.username,
            password: stored.password,
            domain: stored.domain,
            transport: stored.transport,
          };
          usedStoredCredential = true;
        }
      }
    } catch (e) {
      console.warn('[NativeVoice] getStoredCredential failed, falling back to backend fetch:', e?.message || e);
    }
    // Any credential SignalWire (re)issued in THIS call — either because
    // nothing was stored, or because the stored one just failed below —
    // is brand new from SignalWire's perspective and subject to the same
    // propagation race a rotation has (see the retry comment below).
    // Track that explicitly rather than only for the literal /rotate call:
    // a first-ever provision creates the SIP endpoint exactly the same way
    // a rotation does, so a first-time user's very first login hit this
    // identical race with no retry before this fix.
    let freshCredential = false;
    // Reuse the identity check's response instead of a second GET when we
    // already need a fresh credential and it came back fully provisioned.
    if (!cred && expected && expected.username && expected.password) {
      cred = expected;
      freshCredential = true;
    }
    if (!cred) {
      cred = await fetchFreshCredential();
      freshCredential = true;
    }

    const attemptRegister = () => {
      const ready = new Promise((resolve, reject) => {
        this._registerWaiters.push({ resolve, reject });
        setTimeout(() => reject(new Error('NativeVoice: SIP registration timed out')), 20000);
      });
      return NativeVoice.register({
        username: cred.username,
        password: cred.password,
        domain: cred.domain,
        transport: 'tls',
      }).then(() => ready);
    };

    try {
      return await attemptRegister();
    } catch (e) {
      if (usedStoredCredential) {
        // The Keychain copy is stale (e.g. rotated from a different
        // session/device since it was stored) rather than just-issued —
        // no propagation window to wait out here. Get a real fresh one
        // from the backend and fall through to the same retry path
        // a first-time login would take.
        console.warn('[NativeVoice] stored credential rejected, fetching a fresh one:', e.message);
        cred = await fetchFreshCredential();
        freshCredential = true;
      } else if (!freshCredential) {
        throw e;
      }
      // A fresh credential can 401 on the very first REGISTER: the backend
      // creates/rotates the SIP endpoint via SignalWire's REST API and the
      // client sends the SIP REGISTER to their registrar essentially
      // simultaneously, and the two systems are not perfectly synchronous.
      // Measured on device: issuing a credential immediately followed by
      // registration failed with Unauthorized, then succeeded moments later
      // against the SAME credential — the propagation window is variable
      // (observed 401 persisting past a single 1.5s retry, 2026-08-23), so
      // a single retry is not enough. Retry with backoff so a legitimate
      // credential self-heals without ever surfacing an error to the user.
      // ~17s worst case, then fail loudly (App.js shows a friendly error).
      const delays = [2000, 5000, 10000];
      let lastErr = e;
      for (const delay of delays) {
        console.warn(`[NativeVoice] registration failed (${lastErr.message}) — retrying in ${delay}ms`);
        await new Promise((r) => setTimeout(r, delay));
        try {
          return await attemptRegister();
        } catch (retryErr) {
          lastErr = retryErr;
        }
      }
      throw lastErr;
    }
  }

  /**
   * Register a pending session with the backend before dialing, the same
   * way the WebView path's _connectViaConference does via
   * POST /api/outbound/start.
   *
   * The SIP endpoint this device registered with has call_handler=resource
   * pointing at the same cXML webhook (/voice) the WebView path uses — a
   * native call joins the conference either way, this call is not what
   * makes that happen. What it IS needed for: /voice's claiming logic
   * (_db_find_pending_outbound_by_identity / _db_identity_for_sip_username)
   * only has something to claim if a pending row exists, and only a claimed
   * call gets its orchestrator CallSid recorded. Answer-detection polling
   * and live translate key off that CallSid, so without this call they
   * silently never attach to a native call — the call itself still
   * connects and is audible, just without those two features.
   *
   * Best-effort: if this fails, dial anyway with the original `to`. A
   * missing orchestrator hookup is a degraded experience, not a reason to
   * block the call — the same "never strand the user" philosophy as the
   * native-init-failure fallback in createVoiceDevice above.
   */
  async _registerPendingSession(to, from) {
    const { apiBase, authToken } = this._opts;
    if (!apiBase || !authToken) return { dialTo: to, truvaSession: '' };
    try {
      const res = await fetch(`${apiBase}/api/outbound/start`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ to, caller_id: from }),
      });
      if (!res.ok) {
        console.warn(`[NativeVoice] /api/outbound/start ${res.status} — dialing without orchestrator tracking`);
        return { dialTo: to, truvaSession: '' };
      }
      const body = await res.json();
      // Dial the fabric resource address, not the destination number
      // itself. Linphone has no configured outbound-PSTN proxy, so a bare
      // number given to it gets turned into sip:<number>@<our own SIP
      // domain> — the same shape as another Truva user's own SIP endpoint.
      // SignalWire's registrar tries to match that as a specific registered
      // endpoint first; for any number that's ALSO a Fabric resource on
      // this account (i.e. any Truva-hosted number) that lookup finds
      // nothing and the call 422s ("To is not a registered endpoint"),
      // confirmed on device calling one Truva number from another. Dialing
      // the fabric address instead reaches /voice's own dispatch, which
      // resolves the real destination from this pending session via
      // _is_voice_fabric_to — the path every other outbound call already
      // takes, and the only one _to_is_fabric's substitution logic in
      // /voice is built to handle.
      return { dialTo: body.dial_address || body.to || to, truvaSession: body.truva_session || '' };
    } catch (e) {
      console.warn('[NativeVoice] /api/outbound/start failed — dialing without orchestrator tracking:', e);
      return { dialTo: to, truvaSession: '' };
    }
  }

  /**
   * Poll for the orchestrator CallSid /voice assigns once the dialed leg
   * actually lands there (a few seconds after placeCall, not immediate) —
   * see the docstring on _registerPendingSession. Live Translate needs
   * this on the call object the same way it needs pstn_call_sid on the
   * inbound path. Best-effort, bounded: a missing CallSid degrades to "no
   * Live Translate on this call", not a broken call.
   */
  async _pollOrchestratorCallSid(call, truvaSession) {
    const { apiBase, authToken } = this._opts;
    if (!apiBase || !authToken || !truvaSession) return;
    for (let attempt = 0; attempt < 8; attempt++) {
      await new Promise((r) => setTimeout(r, 750));
      if (this._currentCall !== call) return; // call ended/replaced — stop polling
      try {
        const res = await fetch(
          `${apiBase}/api/outbound/orchestrator?truva_session=${encodeURIComponent(truvaSession)}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        if (!res.ok) continue;
        const data = await res.json();
        if (data?.orchestrator_call_sid && this._currentCall === call) {
          call.parameters.CallSid = data.orchestrator_call_sid;
          call.flushPendingDtmf();
          return;
        }
      } catch (_) {
        // transient — keep retrying within the attempt budget
      }
    }
  }

  async connect({ params } = {}) {
    const to = params?.To;
    if (!to) throw new Error('NativeVoice: missing To param');
    if (!this._registered) throw new Error('NativeVoice: not registered');

    if (this._currentCall) {
      try { this._currentCall.disconnect(); } catch (_) {}
      this._currentCall = null;
    }

    // handleCall passes the selected outbound number as CallerId (matching
    // the WebView/Twilio-SDK-style Device.connect({params}) convention) —
    // this was reading params.From instead, which App.js never sets, so
    // every native call silently sent no caller_id at all and the backend
    // fell back to the tenant's first number regardless of what was
    // selected in the UI.
    const callerId = params?.CallerId || params?.From;
    const { dialTo, truvaSession } = await this._registerPendingSession(to, callerId);

    const call = new NativeCall({
      to,
      from: callerId,
      apiBase: this._opts.apiBase,
      authToken: this._opts.authToken,
    });
    this._currentCall = call;
    await NativeVoice.placeCall({ to: String(dialTo) });
    this._pollOrchestratorCallSid(call, truvaSession).catch(() => {});
    return call;
  }

  updateToken() {
    // SIP registration uses its own credential, not the WebRTC SAT.
  }

  _scheduleNativeRefresh(reason) {
    if (this._logoutTeardown) return;
    clearTimeout(this._nativeRefreshTimer);
    this._nativeRefreshTimer = setTimeout(() => {
      this.refreshRegistration({ reason }).catch((e) => {
        console.warn('[NativeVoice] auto refreshRegistration failed:', e?.message || e);
      });
    }, 500);
  }

  /**
   * Fast re-REGISTER from Keychain / Android store. No credential rotate.
   * Safe to call on every Capacitor resume / visibility foreground.
   */
  async refreshRegistration({ reason = 'manual' } = {}) {
    if (this._logoutTeardown) return { refreshed: false, reason: 'logged_out' };
    console.warn(`[NativeVoice] refreshRegistration reason=${reason}`);
    this._ownRegisterUntil = Date.now() + OWN_REGISTER_WINDOW_MS;
    this._registered = false;
    this.emit('unregistered');
    try {
      const result = await NativeVoice.refreshRegistration();
      return result || { refreshed: true };
    } catch (e) {
      // Older native builds may lack refreshRegistration — fall back to
      // full register() which already prefers stored credentials.
      console.warn('[NativeVoice] refreshRegistration plugin missing/failed — falling back to register()', e?.message || e);
      await this.register();
      return { refreshed: true, fallback: 'register' };
    }
  }

  /**
   * @param {{ clearCredentials?: boolean }} [opts]
   * clearCredentials=true ONLY on logout / session wipe. Routine React
   * effect cleanup must leave native SIP registered (product rule).
   */
  destroy({ clearCredentials = false } = {}) {
    this._logoutTeardown = !!clearCredentials;
    clearTimeout(this._registrationFailTimer);
    clearTimeout(this._nativeRefreshTimer);
    try { NativeVoice.hangup(); } catch (_) {}
    if (clearCredentials) {
      // Full teardown — user signed out. Stop recover + wipe stored creds.
      try {
        if (typeof NativeVoice.clearStoredCredential === 'function') {
          NativeVoice.clearStoredCredential();
        } else {
          NativeVoice.unregister();
        }
      } catch (_) {}
    }
    // See the constructor comment: without this, every re-init leaves this
    // instance's plugin-level listeners running forever, alongside whatever
    // new instance gets created next.
    this._listenerHandles.forEach((p) => {
      p.then((handle) => handle.remove()).catch(() => {});
    });
    this._listenerHandles = [];
    this._currentCall = null;
    this._registered = false;
  }
}

export async function createNativeVoiceDevice(opts = {}) {
  return new NativeVoiceDevice(opts);
}

export default NativeVoiceDevice;
